import { createContext, useCallback, useState } from "react";

import { useForm } from "@hilma/netina2all";
import { wrap } from "@hilma/tools";

export const LocationLoadingContext = createContext<boolean | null>(null);
export const AddressLatLngContext = createContext<google.maps.LatLng | undefined | null>(null);
export const MapOptionsContext = createContext<google.maps.MapOptions | null>(null);
export const GeocodeContext = createContext<((requestObj: google.maps.GeocoderRequest, findNewSelectedAddress?: (result: google.maps.GeocoderResult) => void) => void) | null>(null);
export const DeleteLatLngContext = createContext<(() => void) | null>(null);
export const AddressNotAccContext = createContext<[boolean, React.Dispatch<React.SetStateAction<boolean>>] | null>(null);
export const CheckDistanceContext = createContext<((origin: google.maps.LatLng | google.maps.LatLngLiteral, result: google.maps.places.AutocompletePrediction) => void) | null>(null);
export const UpdateMapOptionsContext = createContext<((location: google.maps.LatLng) => void) | null>(null);

export const GeocodeProvider: React.FC = ({ children }) => {
    const [locationLoading, setLocationLoading] = useState<boolean>(false);
    const [addressLatLng, setAddressLatLng] = useState<google.maps.LatLng | undefined>();
    const [mapOptions, setMapOptions] = useState<google.maps.MapOptions>({});
    const [addressNotAcc, setAddressNotAcc] = useState(false);

    const form = useForm();

    // finds the location according to the requestObj and updates the map,
    // calls callback function if one is provided
    const geocode = useCallback(
        (requestObj: google.maps.GeocoderRequest, findNewSelectedAddress?: (result: google.maps.GeocoderResult) => void) => {
            if (!requestObj.placeId && !requestObj.location) return;

            class LatLng extends google.maps.LatLng { };
            const geocoder = new google.maps.Geocoder();

            setLocationLoading(true);
            geocoder.geocode(requestObj, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
                if (status === 'OK') {
                    const { lat, lng } = results[0].geometry.location;
                    const location = new LatLng(lat(), lng());
                    updateMapOptions(location);

                    if (findNewSelectedAddress) findNewSelectedAddress(results[0]);
                }
                setLocationLoading(false);
            });
        },
        []
    );

    const updateMapOptions = useCallback((location: google.maps.LatLng) => {
        setAddressLatLng(location);
        setMapOptions(prev => ({ ...prev, center: { lat: location.lat(), lng: location.lng() }, zoom: 15 }));
        form.setFieldValue('location', { lat: location.lat(), lng: location.lng() });
    }, [addressLatLng, mapOptions]);

    // sometimes the address google maps return is not the right address. 
    // this function checks if the distance between the origin location and the result location is greater than 5km, and if so updates the addressNotAcc state
    const checkDistance = useCallback(
        (origin: google.maps.LatLng | google.maps.LatLngLiteral, result: google.maps.places.AutocompletePrediction) => {
            if (!origin && !result.place_id) return;

            const geocoder = new google.maps.Geocoder();

            geocoder.geocode({ placeId: result.place_id }, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
                if (status === 'OK') {
                    const { lat, lng } = results[0].geometry.location;
                    const originLat: number = isNaN(+origin.lat.toString()) ? (origin.lat as Function)() : origin.lat as number;
                    const originLng: number = isNaN(+origin.lng.toString()) ? (origin.lng as Function)() : origin.lng as number;
                    const distance = Math.sqrt((originLat - lat()) ** 2 + (originLng - lng()) ** 2);
                    if (distance * 100 > 5) setAddressNotAcc(true);
                }
            });
        },
        []
    );

    const deleteLatLng = useCallback(
        () => {
            form.setFieldValue('location', { lat: null, lng: null })
            form.setFieldValue('address', "");
            setAddressLatLng(undefined)
        },
        []
    );

    return wrap(
        [LocationLoadingContext.Provider, { value: locationLoading }],
        [AddressLatLngContext.Provider, { value: addressLatLng }],
        [MapOptionsContext.Provider, { value: mapOptions }],
        [GeocodeContext.Provider, { value: geocode }],
        [DeleteLatLngContext.Provider, { value: deleteLatLng }],
        [AddressNotAccContext.Provider, { value: [addressNotAcc, setAddressNotAcc] }],
        [CheckDistanceContext.Provider, { value: checkDistance }],
        [UpdateMapOptionsContext.Provider, { value: updateMapOptions }]
    )(children) as React.ReactElement;
}